import { type ComponentChildren } from 'preact';
import * as stylex from '@stylexjs/stylex';
import { Sidebar, type NavSection, type SidebarMode, TopBar, type TopBarUser, type TopBarAccount, Scrim } from '@components';
import { styles } from './dash-layout.styles';
interface DashLayoutProps {
  navSections: NavSection[];
  activeKey: string;
  /** Live counts by nav key (unread notifications) — rendered on the matching leaf. */
  navBadges?: Record<string, number> | undefined;
  breadcrumb: readonly [string, string];
  mode: SidebarMode;
  drawerOpen: boolean;
  /** Where the sidebar brand/logo links to — the app's landing page. */
  homeHref?: string;
  onHamburger: () => void;
  onCloseDrawer: () => void;
  /** Opens the ⌘K palette from the top bar (R-346). Absent ⇒ no trigger. */
  onOpenSearch?: (() => void) | undefined;
  searchShortcutLabel?: string | undefined;
  topbarActions?: ComponentChildren;
  /** R-239: force a data refresh from the topbar. Absent ⇒ no button. */
  onRefresh?: (() => void) | undefined;
  refreshing?: boolean | undefined;
  /** The notification bell (wired by the app layer). */
  topbarBell?: ComponentChildren;
  /** Signed-in user — the top bar shows it as the avatar menu (Settings + Logout). */
  user?: TopBarUser | undefined;
  settingsHref?: string;
  onLogout?: (() => void) | undefined;
  /** Multi-account switcher (R-303) — passed straight through to the top bar. */
  accounts?: readonly TopBarAccount[] | undefined;
  onSwitchAccount?: ((id: string) => void) | undefined;
  onSignOutAccount?: ((id: string) => void) | undefined;
  onAddAccount?: (() => void) | undefined;
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
  children: ComponentChildren;
}
const gridFor = {
  full: styles.gridFull,
  rail: styles.gridRail,
  drawer: styles.gridDrawer
} as const;
export function DashLayout({
  navSections,
  activeKey,
  navBadges,
  breadcrumb,
  mode,
  drawerOpen,
  homeHref,
  onHamburger,
  onCloseDrawer,
  onOpenSearch,
  searchShortcutLabel,
  topbarActions,
  onRefresh,
  refreshing,
  topbarBell,
  user,
  settingsHref,
  onLogout,
  accounts,
  onSwitchAccount,
  onSignOutAccount,
  onAddAccount,
  theme,
  onThemeToggle,
  children
}: DashLayoutProps) {
  return <div {...stylex.props(styles.shell, gridFor[mode])}>
      <Sidebar sections={navSections} activeKey={activeKey} mode={mode} drawerOpen={drawerOpen} homeHref={homeHref} onNavigate={mode === 'drawer' ? onCloseDrawer : undefined} badges={navBadges} />
      {mode === 'drawer' && <Scrim show={drawerOpen} onClick={onCloseDrawer} />}
      <div {...stylex.props(styles.main)}>
        <TopBar breadcrumb={breadcrumb} onHamburger={onHamburger} sidebarCollapsed={mode === 'rail'} drawerMode={mode === 'drawer'} drawerOpen={drawerOpen} onOpenSearch={onOpenSearch} searchShortcutLabel={searchShortcutLabel} actions={topbarActions} onRefresh={onRefresh} refreshing={refreshing} bell={topbarBell} theme={theme} onThemeToggle={onThemeToggle} user={user} settingsHref={settingsHref} onLogout={onLogout} accounts={accounts} onSwitchAccount={onSwitchAccount} onSignOutAccount={onSignOutAccount} onAddAccount={onAddAccount} />
        <main {...stylex.props(styles.content)}>{children}</main>
      </div>
    </div>;
}